import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Mic, MicOff, ChevronDown, ChevronRight, Save, Check, FileText, RotateCcw, Cloud, CloudOff, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTO_SAVE_DELAY = 5000; // 5 seconds after last change

const CATEGORY_ICONS = {
    origin: '1', business: '2', sales: '3',
    stories: '4', values: '5', communication: '6', bonus: '7',
};

export default function VoiceCaptureTab() {
    const { getAuthHeader } = useAuth();
    const [questions, setQuestions] = useState({});
    const [answers, setAnswers] = useState({});
    const [expandedCat, setExpandedCat] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [profile, setProfile] = useState(null);
    const [activeQuestion, setActiveQuestion] = useState(null); // {cat, idx}
    const [listening, setListening] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [savedAnswers, setSavedAnswers] = useState({}); // snapshot of last-saved answers
    const recognitionRef = useRef(null);
    const autoSaveTimerRef = useRef(null);
    const answersRef = useRef(answers);
    const savingRef = useRef(false);

    // Keep answersRef in sync
    useEffect(() => { answersRef.current = answers; }, [answers]);

    useEffect(() => { loadData(); return () => clearTimeout(autoSaveTimerRef.current); }, []);

    // Warn on tab close / navigate away with unsaved changes
    useEffect(() => {
        const handler = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasUnsavedChanges]);

    const loadData = async () => {
        try {
            const headers = getAuthHeader();
            const [qRes, pRes] = await Promise.all([
                axios.get(`${API}/voice-profile/questions`, { headers }),
                axios.get(`${API}/voice-profile`, { headers }),
            ]);
            setQuestions(qRes.data);
            const loadedAnswers = pRes.data.answers || {};
            setAnswers(loadedAnswers);
            setSavedAnswers(JSON.parse(JSON.stringify(loadedAnswers)));
            setProfile(pRes.data);
            if (pRes.data.updated_at) setLastSavedAt(new Date(pRes.data.updated_at));
            // Auto-expand first incomplete category
            for (const key of Object.keys(qRes.data)) {
                const catAnswers = pRes.data.answers?.[key] || {};
                const allAnswered = qRes.data[key].questions.every((_, i) => catAnswers[String(i)]?.trim());
                if (!allAnswered) { setExpandedCat(key); break; }
            }
        } catch (err) { console.error('Voice profile load error:', err?.response?.status, err?.response?.data || err?.message); toast.error('Failed to load voice profile'); }
        finally { setLoading(false); }
    };

    const doSave = async (answersToSave, isAutoSave = false) => {
        if (savingRef.current) return; // prevent concurrent saves
        savingRef.current = true;
        setSaving(true);
        try {
            const res = await axios.put(`${API}/voice-profile`, { answers: answersToSave }, { headers: getAuthHeader() });
            setProfile(res.data);
            setSavedAnswers(JSON.parse(JSON.stringify(answersToSave)));
            setHasUnsavedChanges(false);
            setLastSavedAt(new Date());
            if (!isAutoSave) toast.success('Voice profile saved');
        } catch {
            if (!isAutoSave) toast.error('Failed to save');
            else console.error('Auto-save failed');
        }
        finally { savingRef.current = false; setSaving(false); }
    };

    const handleSave = () => {
        clearTimeout(autoSaveTimerRef.current);
        doSave(answersRef.current, false);
    };

    const scheduleAutoSave = useCallback(() => {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = setTimeout(() => {
            doSave(answersRef.current, true);
        }, AUTO_SAVE_DELAY);
    }, []);

    const setAnswer = (cat, idx, value) => {
        setAnswers(prev => ({
            ...prev,
            [cat]: { ...(prev[cat] || {}), [String(idx)]: value },
        }));
        setHasUnsavedChanges(true);
        scheduleAutoSave();
    };

    // Web Speech API
    const startListening = useCallback((cat, idx) => {
        if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
            return toast.error('Voice dictation not supported in this browser. Try Chrome.');
        }
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        let finalTranscript = answers[cat]?.[String(idx)] || '';

        recognition.onresult = (event) => {
            let interim = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
                if (event.results[i].isFinal) {
                    finalTranscript += (finalTranscript ? ' ' : '') + event.results[i][0].transcript;
                } else {
                    interim += event.results[i][0].transcript;
                }
            }
            setAnswer(cat, idx, finalTranscript + (interim ? ' ' + interim : ''));
        };
        recognition.onerror = () => { setListening(false); };
        recognition.onend = () => { setListening(false); };

        recognition.start();
        recognitionRef.current = recognition;
        setListening(true);
        setActiveQuestion({ cat, idx });
    }, [answers]);

    const stopListening = () => {
        if (recognitionRef.current) {
            recognitionRef.current.stop();
            recognitionRef.current = null;
        }
        setListening(false);
    };

    // Compute stats
    const totalQuestions = Object.values(questions).reduce((s, c) => s + c.questions.length, 0);
    const answeredCount = Object.entries(answers).reduce((s, [cat, ca]) =>
        s + Object.values(ca).filter(v => v && String(v).trim()).length, 0);
    const completionPct = totalQuestions ? Math.round(answeredCount / totalQuestions * 100) : 0;

    const getCatCompletion = (catKey) => {
        const cat = questions[catKey];
        if (!cat) return { answered: 0, total: 0 };
        const catAnswers = answers[catKey] || {};
        const answered = cat.questions.filter((_, i) => catAnswers[String(i)]?.trim()).length;
        return { answered, total: cat.questions.length };
    };

    if (loading) return <div className="flex items-center justify-center h-48"><div className="w-8 h-8 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" /></div>;

    return (
        <div className="space-y-6" data-testid="voice-capture-tab">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Voice of the User</h2>
                    <p className="text-sm text-stone-500 mt-0.5">Answer these questions to train your personal AI voice. Use the microphone for voice dictation.</p>
                    <p className="text-xs text-stone-400 mt-1">Your answers auto-save as you type.</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                    {/* Auto-save status */}
                    <div className="flex items-center gap-1.5 text-xs">
                        {saving ? (
                            <><Loader2 className="w-3.5 h-3.5 text-stone-400 animate-spin" /><span className="text-stone-400">Saving...</span></>
                        ) : hasUnsavedChanges ? (
                            <><CloudOff className="w-3.5 h-3.5 text-amber-500" /><span className="text-amber-600">Unsaved changes</span></>
                        ) : lastSavedAt ? (
                            <><Cloud className="w-3.5 h-3.5 text-emerald-500" /><span className="text-emerald-600">Saved</span></>
                        ) : null}
                    </div>
                    <Button onClick={handleSave} disabled={saving} className="bg-stone-900 hover:bg-stone-800" data-testid="save-voice-profile">
                        <Save className="w-4 h-4 mr-1.5" />{saving ? 'Saving...' : 'Save Profile'}
                    </Button>
                </div>
            </div>

            {/* Progress Bar */}
            <div className="bg-white rounded-xl border border-stone-200 p-4" data-testid="voice-progress">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-stone-700">{answeredCount} of {totalQuestions} questions answered</span>
                    <span className="text-sm font-bold text-stone-700">{completionPct}%</span>
                </div>
                <div className="w-full bg-stone-100 rounded-full h-2.5 overflow-hidden">
                    <div className="h-full bg-stone-900 rounded-full transition-all duration-500" style={{ width: `${completionPct}%` }} />
                </div>
            </div>

            {/* Categories */}
            <div className="space-y-3">
                {Object.entries(questions).map(([catKey, cat]) => {
                    const isExpanded = expandedCat === catKey;
                    const { answered, total } = getCatCompletion(catKey);
                    const isComplete = answered === total;

                    return (
                        <div key={catKey} className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid={`category-${catKey}`}>
                            {/* Category Header */}
                            <button onClick={() => setExpandedCat(isExpanded ? null : catKey)}
                                className="w-full flex items-center gap-3 p-4 text-left hover:bg-stone-50 transition-colors" data-testid={`toggle-${catKey}`}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${isComplete ? 'bg-emerald-100 text-emerald-600' : 'bg-stone-100 text-stone-700'}`}>
                                    {isComplete ? <Check className="w-4 h-4" /> : CATEGORY_ICONS[catKey]}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-sm font-semibold text-stone-800">{cat.title}</h3>
                                        <span className="text-xs text-stone-400">{answered}/{total}</span>
                                    </div>
                                    <p className="text-xs text-stone-400 mt-0.5 truncate">{cat.description}</p>
                                </div>
                                {isExpanded ? <ChevronDown className="w-4 h-4 text-stone-400" /> : <ChevronRight className="w-4 h-4 text-stone-400" />}
                            </button>

                            {/* Questions */}
                            {isExpanded && (
                                <div className="border-t border-stone-100 divide-y divide-stone-100">
                                    {cat.questions.map((q, idx) => {
                                        const answer = answers[catKey]?.[String(idx)] || '';
                                        const savedAnswer = savedAnswers[catKey]?.[String(idx)] || '';
                                        const isSaved = answer.trim() && answer === savedAnswer;
                                        const isUnsaved = answer.trim() && answer !== savedAnswer;
                                        const isActive = activeQuestion?.cat === catKey && activeQuestion?.idx === idx;
                                        const isRecording = listening && isActive;
                                        return (
                                            <div key={idx} className={`p-4 ${isRecording ? 'bg-red-50/50' : ''}`} data-testid={`question-${catKey}-${idx}`}>
                                                <div className="flex items-start gap-3">
                                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5 ${answer.trim() ? 'bg-emerald-100 text-emerald-600' : 'bg-stone-100 text-stone-400'}`}>
                                                        {answer.trim() ? <Check className="w-3 h-3" /> : idx + 1}
                                                    </span>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-stone-700 mb-2">{q}</p>
                                                        <div className="relative">
                                                            <textarea
                                                                value={answer}
                                                                onChange={e => setAnswer(catKey, idx, e.target.value)}
                                                                rows={answer.length > 200 ? 5 : 3}
                                                                placeholder="Type your answer or use the microphone..."
                                                                data-testid={`answer-${catKey}-${idx}`}
                                                                className={`w-full px-3 py-2 text-sm border rounded-lg resize-y focus:outline-none focus:ring-2 ${isRecording ? 'border-red-300 focus:ring-red-200 bg-red-50/30' : 'border-stone-200 focus:ring-stone-900/20 focus:border-stone-400'}`}
                                                            />
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-2">
                                                            {!isRecording ? (
                                                                <button onClick={() => startListening(catKey, idx)}
                                                                    className="flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-700 px-2.5 py-1.5 rounded-md hover:bg-stone-100 transition-colors"
                                                                    data-testid={`mic-${catKey}-${idx}`}>
                                                                    <Mic className="w-3.5 h-3.5" />Dictate
                                                                </button>
                                                            ) : (
                                                                <button onClick={stopListening}
                                                                    className="flex items-center gap-1.5 text-xs text-red-600 px-2.5 py-1.5 rounded-md bg-red-50 hover:bg-red-100 animate-pulse"
                                                                    data-testid={`mic-stop-${catKey}-${idx}`}>
                                                                    <MicOff className="w-3.5 h-3.5" />Stop Recording
                                                                </button>
                                                            )}
                                                            {answer.trim() && (
                                                                <button onClick={() => setAnswer(catKey, idx, '')}
                                                                    className="flex items-center gap-1 text-xs text-stone-400 hover:text-red-500 px-2 py-1.5 rounded-md hover:bg-red-50"
                                                                    data-testid={`clear-${catKey}-${idx}`}>
                                                                    <RotateCcw className="w-3 h-3" />Clear
                                                                </button>
                                                            )}
                                                            <span className="text-xs ml-auto flex items-center gap-1.5">
                                                                {answer.length > 0 && <span className="text-stone-300">{answer.length} chars</span>}
                                                                {saving && isUnsaved ? (
                                                                    <span className="text-stone-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Saving</span>
                                                                ) : isSaved ? (
                                                                    <span className="text-emerald-500 flex items-center gap-1"><Check className="w-3 h-3" />Saved</span>
                                                                ) : isUnsaved ? (
                                                                    <span className="text-amber-500 flex items-center gap-1"><CloudOff className="w-3 h-3" />Unsaved</span>
                                                                ) : null}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Bottom Save */}
            <div className="flex items-center justify-end gap-3 pt-2">
                {saving ? (
                    <span className="text-xs text-stone-400 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" />Saving...</span>
                ) : hasUnsavedChanges ? (
                    <span className="text-xs text-amber-600 flex items-center gap-1.5"><CloudOff className="w-3.5 h-3.5" />Unsaved changes</span>
                ) : lastSavedAt ? (
                    <span className="text-xs text-emerald-600 flex items-center gap-1.5"><Cloud className="w-3.5 h-3.5" />All changes saved</span>
                ) : null}
                <Button onClick={handleSave} disabled={saving} className="bg-stone-900 hover:bg-stone-800" data-testid="save-voice-profile-bottom">
                    <Save className="w-4 h-4 mr-1.5" />{saving ? 'Saving...' : 'Save Voice Profile'}
                </Button>
            </div>
        </div>
    );
}
